import React from 'react';
import { Document, Page, Text, View, StyleSheet, Image } from '@react-pdf/renderer';
import { Volunteer } from '../types/sharepoint';

const styles = StyleSheet.create({
  page: {
    padding: 15,
    fontSize: 8,
    backgroundColor: 'white',
  },
  header: {
    textAlign: 'center',
    marginBottom: 10,
    borderBottom: '2px solid #0078d4',
    paddingBottom: 6,
  },
  logo: {
    width: 60,
    height: 40,
    alignSelf: 'center',
    marginBottom: 8,
  },
  clinicName: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#0078d4',
    marginBottom: 2,
  },
  clinicInfo: {
    fontSize: 10,
    color: '#666',
    marginBottom: 1,
  },
  arrivalTime: {
    fontSize: 9,
    color: '#888',
    fontWeight: 'bold',
  },
  unassigned: {
    fontStyle: 'italic',
    color: '#999',
    fontSize: 7,
  },
  scheduleGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 12,
    justifyContent: 'space-between',
  },
  sectionCard: {
    width: '31%',
    border: '1px solid #e0e0e0',
    borderRadius: 4,
    padding: 8,
    marginBottom: 8,
    backgroundColor: 'white',
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 6,
    paddingBottom: 3,
    borderBottom: '1px solid #ddd',
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#333',
  },
  roleItem: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '2px 0',
    borderBottom: '1px solid #eee',
  },
  roleName: {
    fontSize: 7,
    color: '#333',
    flex: 1,
  },
  assignmentArea: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  assignedVolunteer: {
    alignItems: 'flex-end',
  },
  volunteerName: {
    fontSize: 6,
    color: '#0078d4',
    fontWeight: 'bold',
  },
  volunteerArea: {
    fontSize: 5,
    color: '#666',
    fontStyle: 'italic',
  },
});

interface Assignment {
  roleId: string;
  volunteerId: string;
  volunteerName: string;
  volunteerAvatar: string;
}

interface ScheduleSection {
  id: string;
  title: string;
  roles: Array<{
    id: string;
    name: string;
    description?: string;
  }>;
}

interface SchedulePdfProps {
  sections: ScheduleSection[];
  assignments: Assignment[];
  volunteers: Volunteer[];
  clinicDate: Date;
  arrivalTime: string;
}

export const SchedulePdf: React.FC<SchedulePdfProps> = ({ 
  sections, 
  assignments, 
  volunteers, 
  clinicDate, 
  arrivalTime 
}) => {


  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <Image src="/color-trucare.jpg" style={styles.logo} />
          <Text style={styles.clinicName}>TruCare Clinic</Text>
          <Text style={styles.clinicInfo}>Clinic Schedule</Text>
          <Text style={styles.clinicInfo}>
            {clinicDate.toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </Text>
          <Text style={styles.arrivalTime}>Arrival Time: {arrivalTime}</Text>
        </View>

        {/* Schedule Grid - Matching Preview Layout */}
        <View style={styles.scheduleGrid}>
          {sections.map((section) => {
            return (
              <View key={section.id} style={styles.sectionCard}>
                <View style={styles.sectionHeader}>
                  <Text style={styles.sectionTitle}>{section.title}</Text>
                </View>
                
                {section.roles.map((role) => {
                  const assignment = assignments.find(a => a.roleId === role.id);
                  const volunteer = assignment ? volunteers.find(v => v.id === assignment.volunteerId) : null;
                  
                                     return (
                     <View key={role.id} style={styles.roleItem}>
                       <Text style={styles.roleName}>{role.name}</Text>
                       <View style={styles.assignmentArea}>
                         {volunteer ? (
                           <View style={styles.assignedVolunteer}>
                             <Text style={styles.volunteerName}>
                               {volunteer.name.split(' ').slice(0, 2).join(' ')}
                             </Text>
                           </View>
                         ) : (
                           <Text style={styles.unassigned}>Unassigned</Text>
                         )}
                       </View>
                     </View>
                   );
                })}
              </View>
            );
          })}
        </View>
      </Page>
    </Document>
  );
}; 